<template lang="pug">
	.ssml-web-service-editor.flex-row.space-large.full-width
		.flex-col.space-large(v-if="!hideServiceSelection")
			.flex-col
				.upper.grey.font-small {{$tCaption('Web-service')}}
				Dropdown(:getTotal="(r) => r.TotalNumberOfRecords", :getItems="(r) => r.Results", :getData="$Data.getExternalServices",
					:columns="[{ dataField: 'Name', caption: $tCaption('Service'), cellTemplate: 'name-template' }]",
					displayExpr="Name",
					valueExpr="Oid",
					placeholder="Web-service",
					v-model="innerValue.id",
					:showColumnHeaders="false",
					:filtersVisible="false",
					:searchVisible="true",
					:multiple="false")
						dx-validator
							dx-required-rule(:message="$tCaption('Service is required')")
						template(v-slot:name-template="{ data }")
							.flex-col
								span {{data.Name}}
								.font-small.grey.description.text-overflow(v-if="data.JsonDefinition && data.JsonDefinition.Description") {{data.JsonDefinition.Description}}
				.font-small(v-if="service")
					.grey.description.text-overflow(v-if="service.Description") {{service.Description}}
					a.documentation-url.text-overflow(v-if="service.Description", :href="service.DocumentationUrl", target="_blank") {{$tCaption('Documentation')}}
			.flex-col(v-if="service && !isLoadingService")
				.upper.grey.font-small {{$tCaption('Speech value')}}
				Dropdown(ref="outparam", :getTotal="(r) => r.length", :getItems="(data) => data", :getData="getServiceOutputs",
					:columns="[{ dataField: 'Text', caption: $tCaption('Name') }]",
					displayExpr="Text",
					valueExpr="Name",
					:placeholder="$tCaption('Select parameter')",
					v-model="innerValue.outparam",
					:showColumnHeaders="false",
					:filtersVisible="false",
					:searchVisible="true",
					:multiple="false",
					@select-data="v => { selectedOutParam = v; innerValue.outparam = v && v.Name || null; }")
					dx-validator
						dx-required-rule(:message="$tCaption('Parameter is required')")
		.flex-row.align-center(v-if="isLoadingService && innerValue.id")
			Loader(:text="$tCaption('Loading', '@actions')")
		.flex-col.space-medium.full-width(v-if="criteriaVisible")
			.flex-col.space(v-if="service")
				a.font-small.text-overflow(v-if="service.OwnerOid === $auth.companyId()", @click="openService") {{service.Name}}
				.font-small.text-overflow(v-else) {{service.Name}}
				.font-small(v-if="service.Description")
					.grey.description.text-overflow {{service.Description}}
					a.documentation-url.text-overflow(:href="service.DocumentationUrl", target="_blank") {{$tCaption('Documentation')}}
			template(v-if="selectedOutParam && selectedOutParam.IsArray")
				.flex-row
					.adt-tab.flex-row.align-center.center(:class="{ active: tab === 'parameters' }", @click="tab = 'parameters'") {{$tCaption('Parameters')}}
					.adt-tab.flex-row.align-center.center(:class="{ active: tab === 'listOptions' }", @click="tab = 'listOptions'") {{$tCaption('Phrases builder')}}
			.flex-row(v-show="tab === 'parameters'")
				WBSCritera.ssml-web-service-editor--criteria(ref="criteriaEditor", :service="service", :hideCriteria="true", :params="innerValue.inparam", :paramsCaptions="innerValue.inparamCaptions", 
					:onlyEditableParams="true", :allowKeyEditing="allowKeyEditing", @change="onWbsParamsChange", :outputParamName="innerValue.outparam", :usedServices="usedServices",
					:associations="innerValue.templateFieldsAssociation", @template-field-change="(param, templateFieldId) => $set(innerValue.templateFieldsAssociation, param.Name, templateFieldId)",
					:allowTemplateFieldCreation="allowKeyEditing", @action="onCriteriaAction", :fullStructure="true", :hideHeaders="selectedOutParam && selectedOutParam.IsArray", :preventStoreParameters="preventStoreParameters")
					template(v-if="selectedOutParam && selectedOutParam.ListOptions && selectedOutParam.ListOptions.length", slot="parameters")
						.flex-row(v-if="!customizeOutputList")
							DxButton(:text="$tCaption('Customize output values')", @click="() => customizeOutputList = true")
						template(v-else)
							.flex-col.space-medium
								.flex-col
									.font-small.upper.grey {{$tCaption('Default text (if option cannot be evaluated)')}}
									.lr-padding.flex-col.space-medium
										DxTextBox(:value.sync="innerValue.defaultText")
								.font-small.upper.grey {{$tCaption('Custom values')}}
								.lr-padding.flex-col.space-medium
									.custom-value.flex-col.field(v-for="value in customizeOutputListValues")
										.flex-row.align-center.grey {{value.Key}}:
										.flex-row.space.align-center
											DxTextBox(:placeholder="value.text", :value.sync="value.value")
			template(v-if="allowKeyEditing && selectedOutParam && selectedOutParam.DataType === 'media'")
				.flex-col.space-medium
					.font-big.upper.grey {{ $tCaption('Media options') }}
					.lr-padding.flex-row
						DxCheckBox(:text="$tCaption('New insertion')", :value.sync="innerValue.newInsertion", 
							:hint="$tCaption('If True - the system will remove the current SsmlInsertion and replace it with a new AudioInsertion using calculated URL from output, otherwise the audio from media output will be added into the current SsmlInsertion as <audio src=\"\">')")
			template(v-if="tab === 'listOptions'")
				ParamListOptionsEditor.lr-padding(v-if="selectedOutParam", :output="selectedOutParam", :service="service", :options="innerValue.listOptions", @change="v => innerValue.listOptions = v")
</template>

<script>
	import _ from 'lodash';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import Loader from 'app-core/Loader.vue';
	import WBSCritera from 'view/webServiceCaller/StructureCriteria.vue';
	import Dropdown from 'app-core/Dropdown.vue';
	import Promise from 'bluebird';
	import DxValidationGroup from 'devextreme-vue/validation-group';
	import DxValidator, { DxRequiredRule, DxCustomRule } from 'devextreme-vue/validator';
	import DxSelectBox from 'devextreme-vue/select-box';
	import serviceHelper from 'view/webServiceCaller/service-helper';
	import ParamListOptionsEditor from 'view/webServiceCaller/ParamListOptionsEditor.vue';

	export default {
		translationCategory: 'insertion.SSMLWebServiceEditor',
		name: 'ssml-web-service-editor',
		components: {
			DxButton, Dropdown, Loader, WBSCritera, DxTextBox, DxCheckBox, DxValidationGroup, DxValidator, DxRequiredRule, DxCustomRule, DxSelectBox, ParamListOptionsEditor
		},
		model: {
			prop: 'value',
			event: 'change'
		},
		props: ['text', 'value', 'allowKeyEditing', 'templateFields', 'usedServices', 'preventStoreParameters'],
		data() {
			return {
				innerValue: _.merge({
					id: null,
					outparam: null,
					inparam: {},
					inparamCaptions: {},
					defaultText: null,
					customValues: null,
					templateField: null,
					templateFieldsAssociation: {},
					listOptions: {},
					newInsertion: false,
					selectedParam: null
				}, _.clone(this.value || {})),
				service: null,
				selectedOutParam: null,
				customizeOutputList: false,
				customizeOutputListValues: _.cloneDeep(this.value?.customValues || []),
				isLoadingService: false,
				hideServiceSelection: this.value?.hideServiceSelection,
				tab: 'parameters'
			};
		},
		computed: {
			customValues() {
				return (this.customizeOutputListValues || []).filter(f => f.value).map(f => ({ Key: f.key, Value: f.value }));
			},
			result() {
				return { ...this.innerValue, customValues: this.customValues };
			},
			criteriaVisible() {
				return this.service && this.innerValue.outparam && !this.isLoadingService && ((this.service?.JsonDefinition?.Parameters || []).filter(f => f.UserEditable).length || this.service.JsonDefinition?.ComplexParameters?.length);
			}
		},
		watch: {
			tab(v) {
				this.$emit('show-params-list-options', v === 'listOptions');
			},
			selectedOutParam(v) {
				if (!this.isLoadingService)
					this.customizeOutputListValues = _.cloneDeep(v?.ListOptions?.map(f => ({ key: f.Key, value: null, text: f.Value })));
			},
			'innerValue.id'() {
				this.loadService(true);
			},
			'innerValue': {
				handler() {
					if (__DEBUG__)
						console.debug('Inner value changed');
					this.emitChange();
				},
				deep: true
			},
			customValues: {
				handler() {
					this.emitChange();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.ssmlWbsEditor = this;
			this.loadService();
		},
		methods: {
			onCriteriaAction(action, param) {
				switch(action) {
					case 'create-template-field': {
						if (__DEBUG__)
							console.debug('Create template field from param', { param });
						const templateField = serviceHelper.getTemplateFieldFromParam(param);

						this.$emit('create-template-field', templateField, (result) => {
							//Link template field immediate
							if (result?.id) {
								this.$set(this.innerValue.templateFieldsAssociation, param.Name, result.id)
							}
						});
						break;
					}
				}
			},
			openService() {
				let routeData = this.$router.resolve({ path: `/wbs/Builder/${this.service?.Oid}` });
				window.open(routeData.href, '_blank');
			},
			validate() {
				return this.$refs.criteriaEditor.validate();
			},
			onWbsParamsChange(criteria, params, paramsCaptions) {
				if (__DEBUG__)
					console.debug('onWbsParamsChange', { criteria, params, paramsCaptions });
				this.innerValue.inparam = params;
				this.innerValue.inparamCaptions = paramsCaptions;
			},
			emitChange() {
				this.$emit('change', this.result);
			},
			loadService(reload) {
				if (this.innerValue.id) {
					this.isLoadingService = true;
					if (reload) {
						this.innerValue = {
							id: this.innerValue.id,
							outparam: null,
							inparam: {},
							defaultText: null,
							customValues: null,
							templateField: null,
							templateFieldsAssociation: {}
						}
					}
					this.$Data.getExternalServices(0, 1, { byKey: this.innerValue.id }).then(r => this.service = r.Results[0]).then(r => {
						this.selectedOutParam = this.service?.JsonDefinition.Outputs?.filter(f => f.Name === this.innerValue.outparam)[0];
						this.innerValue.selectedParam = this.selectedOutParam;
						this.customizeOutputListValues = _.cloneDeep(this.selectedOutParam?.ListOptions.map(f => ({ key: f.Key, value: null, text: f.Value })));
						(this.customizeOutputListValues || []).map(f => {
							var item = _.find(this.innerValue.customValues, v => v.Key === f.key);
							if (item && item.Value)
								f.value = item.Value;
						});
						this.customizeOutputList = !!this.customValues.length || !!this.innerValue.defaultText;
					}).finally(() => this.isLoadingService = false);
				} else
					this.service = null;
			},
			getServiceOutputs(pageIndex, limit) {
				var items = (this.service?.JsonDefinition.Outputs || []).filter(f => !f.Status).map(f => ({ ...f, Text: f.Description || f.Name }));
				return Promise.resolve(items.slice(pageIndex * limit, pageIndex * limit + limit));
			}
		}
	};
</script>
<style lang='scss'>
	.ssml-web-service-editor {
		.dx-invalid-message.dx-invalid-message-auto {
			> * {
				transform: translate(0px,-10px) !important;
			}
		}

		.documentation-url, .description {
			max-width: 200px;
		}
	}

	.ssml-web-service-editor--criteria {
		padding: 0px !important;
	}
</style>
